import type { LensCard, LensDataset, LensFilter } from '~/types/leLens';

import type { Ask, LensTurnEffort } from './buildSession';
import type { Check } from './checkSql';
import { applyDatasetChange } from './datasetChange';
import {
  type DatasetExtendCheckResult,
  describeExtendCheckPass,
  describeExtendCheckRefusal,
  extendCheckRefused,
  runDatasetExtendCheck,
} from './datasetExtendCheck';
import { readableColumns, viewHoldsOn } from './datasetView';
import {
  type DatasetChange,
  type GeneratedCardTier,
  type GeneratedLensCard,
  generateLensCard,
  makeUniqueCardId,
} from './generateLensCard';
import type { BuildPool, LensAskLane } from './lensAskSession';
import type { GateRow, GateRowFailure } from './planGateResolve';
import type { VerifiedSource } from './prompts';

/** What a row's chip should say while and after its query is written. */
export type CardWritingOutcome = 'writing' | 'ready' | 'failed' | 'stopped';

/** What a row's build decided, and what a refused extension leaves the reader
 *  to escalate. `guard` is absent when no saved Lens existed to check against. */
export type CardDecision = {
  tier: GeneratedCardTier['tier'];
  sentence: string;
  guard?: { result: DatasetExtendCheckResult; refused: boolean };
  escalation?: { card: GeneratedLensCard; change: Extract<DatasetChange, { kind: 'extend' }> };
};

/**
 * Writes a query for each card the shared datasets could not answer.
 *
 * Concurrent, because what is left after grouping is one row per grain and no
 * row waits on another's answer. What they do share is the board's datasets,
 * which a card may ask to extend or to carry a calculated field: those changes
 * are applied in row order once every branch is back, and a bound card whose
 * view no longer holds against the dataset as it finally stands fails rather
 * than rendering a column nothing returns. Ids are allocated in row order too,
 * so the same plan yields the same board whichever lane finished first.
 */
export async function buildCards({
  rows,
  datasets,
  existingIds,
  filters,
  pool,
  effort,
  onStatus,
  onRow,
  onDecision,
  abandoned,
  sources,
  check,
  lensId,
}: {
  rows: ReadonlyArray<GateRow>;
  datasets: ReadonlyArray<LensDataset>;
  existingIds: ReadonlyArray<string>;
  filters: ReadonlyArray<LensFilter>;
  pool: BuildPool;
  /** Writing SQL to a settled specification is mechanical; deciding it is not. */
  effort?: LensTurnEffort;
  onStatus: (message: string | null) => void;
  onRow?: (row: GateRow, outcome: CardWritingOutcome) => void;
  onDecision?: (row: GateRow, decision: CardDecision) => void;
  /** Asked before each row starts, so a reader's stop is honoured mid-wave. */
  abandoned?: () => boolean;
  /** Tables the grouping turn looked up, handed to every branch instead of each finding them again. */
  sources?: ReadonlyArray<VerifiedSource>;
  /** Supplied when the query is checked against the warehouse rather than by the model itself. */
  check?: Check;
  /** The saved Lens an extension can be checked against. A dashboard still being created has
   *  nothing saved yet, so its extensions are applied without a guard. */
  lensId?: string;
}): Promise<{
  cards: Array<LensCard>;
  rowIdByCardId: ReadonlyMap<string, string>;
  datasets: Array<LensDataset>;
  failures: Array<GateRowFailure>;
  stopped: Array<GateRow>;
}> {
  if (!rows.length) {
    return { cards: [], rowIdByCardId: new Map(), datasets: [...datasets], failures: [], stopped: [] };
  }

  onStatus(
    rows.length === 1 ? `Writing the query for “${rows[0].title}”…` : `Writing ${rows.length} more queries at once…`,
  );

  const offered = datasets.map((dataset) => ({
    id: dataset.id,
    name: dataset.name,
    columns: readableColumns(dataset),
    ...(dataset.columns?.length ? { declaredColumns: dataset.columns } : {}),
    reads: [dataset.sql, dataset.source?.table].filter(Boolean).join('\n'),
  }));

  const written = await pool.map<GateRow, WrittenCard>(rows, async (row, lane) => {
    if (abandoned?.()) {
      onRow?.(row, 'stopped');
      return { row, stopped: true };
    }
    onRow?.(row, 'writing');
    const result = await generateLensCard({
      request: row.request,
      type: row.type,
      datasets: offered,
      filters: [...filters],
      existingIds: [...existingIds],
      onStatus: () => {},
      ask: laneAsk(lane, effort),
      ...(sources?.length ? { sources } : {}),
      ...(check ? { check } : {}),
    });
    return { row, stopped: false, ...result };
  });

  let changed = [...datasets];
  const taken = new Set(existingIds);
  const admitted: Array<{ row: GateRow; card: LensCard; extendedSql?: string }> = [];
  const failures: Array<GateRowFailure> = [];
  const stopped: Array<GateRow> = [];

  for (const branch of written) {
    if (!branch) {
      continue;
    }
    const { row } = branch;
    if (branch.stopped) {
      stopped.push(row);
      continue;
    }
    const { card, change, error } = branch;
    if (!card) {
      failures.push({ rowId: row.id, title: row.title, reason: error ?? 'The query could not be written.' });
      onRow?.(row, 'failed');
      continue;
    }
    const refusal = await refuseUnsafeExtension({ card, change, datasets: changed, lensId, onStatus, onDecision, row });
    if (refusal) {
      failures.push(refusal);
      onRow?.(row, 'failed');
      continue;
    }
    changed = applyDatasetChange(changed, change);
    if (branch.dataset) {
      changed = [...changed, branch.dataset];
    }
    const id = makeUniqueCardId(row.request || row.title || card.id, [...taken]);
    taken.add(id);
    admitted.push({
      row,
      card: asLensCard(card, id),
      ...(change?.kind === 'extend' ? { extendedSql: change.sql } : {}),
    });
    onRow?.(row, 'ready');
  }

  const cards: Array<LensCard> = [];
  const rowIdByCardId = new Map<string, string>();
  for (const { row, card, extendedSql } of admitted) {
    const boundTo = card.datasetId ? changed.find((dataset) => dataset.id === card.datasetId) : undefined;
    const unbound = card.datasetId ? refusalToBind(card, boundTo, extendedSql) : undefined;
    if (unbound) {
      failures.push({ rowId: row.id, title: row.title, reason: unbound });
      onRow?.(row, 'failed');
      continue;
    }
    cards.push(card);
    rowIdByCardId.set(card.id, row.id);
  }

  return { cards, rowIdByCardId, datasets: changed, failures, stopped };
}

type WrittenCard = {
  row: GateRow;
  /** True when the reader pressed Stop before this row's turn began. */
  stopped: boolean;
  card?: GeneratedLensCard;
  change?: DatasetChange;
  /** A brand new one-card dataset, minted when nothing on the board could answer the row. */
  dataset?: LensDataset;
  error?: string;
};

/**
 * The extend guard: an extension is dry run against the saved Lens and every card already reading
 * the dataset is re-shaped, before the rewrite is trusted with anything else on the board.
 *
 * Returns the row's failure when the guard refused, so a grain or number change is never applied
 * without the reader seeing it, and nothing at all when the change is safe or is not an extension.
 */
async function refuseUnsafeExtension({
  card,
  change,
  datasets,
  lensId,
  onStatus,
  onDecision,
  row,
}: {
  card: GeneratedLensCard;
  change?: DatasetChange;
  datasets: ReadonlyArray<LensDataset>;
  lensId?: string;
  onStatus: (message: string | null) => void;
  onDecision?: (row: GateRow, decision: CardDecision) => void;
  row: GateRow;
}): Promise<GateRowFailure | undefined> {
  if (change?.kind !== 'extend' || !lensId) {
    if (card.tier) {
      onDecision?.(row, { tier: card.tier.tier, sentence: card.tier.decision });
    }
    return undefined;
  }

  const extended = datasets.find((dataset) => dataset.id === change.datasetId);
  onStatus(`Checking “${extended?.name ?? change.datasetId}”…`);
  let result: DatasetExtendCheckResult;
  try {
    result = await runDatasetExtendCheck(lensId, change.datasetId, change.sql, change.grain);
  } catch (error) {
    return {
      rowId: row.id,
      title: row.title,
      reason: error instanceof Error ? error.message : 'Could not check this extension.',
    };
  }

  const refused = extendCheckRefused(result);
  if (card.tier) {
    onDecision?.(row, {
      tier: card.tier.tier,
      sentence: refused
        ? describeExtendCheckRefusal(result)
        : describeExtendCheckPass(result, extended?.columns?.length ?? 0),
      guard: { result, refused },
      ...(refused ? { escalation: { card, change } } : {}),
    });
  }

  return refused
    ? {
        rowId: row.id,
        title: row.title,
        reason: `${describeExtendCheckRefusal(result)} Ask for a new dataset instead.`,
      }
    : undefined;
}

function refusalToBind(
  card: LensCard,
  dataset: LensDataset | undefined,
  extendedSql: string | undefined,
): string | undefined {
  if (!dataset || (extendedSql !== undefined && dataset.sql !== extendedSql)) {
    return 'The dataset it read changed while it was being written.';
  }
  return viewHoldsOn(card.view, dataset) ? undefined : 'It asked for a column its dataset does not return.';
}

function laneAsk(lane: LensAskLane, effort?: LensTurnEffort): Ask {
  return (question, options) => {
    const chosen = options?.effort ?? effort;
    return lane.ask(question, { ...(chosen ? { effort: chosen } : {}) });
  };
}

function asLensCard(card: GeneratedLensCard, id: string): LensCard {
  return {
    id,
    type: card.type,
    title: card.title,
    description: card.description,
    ...(card.insightQuestion ? { insightQuestion: card.insightQuestion } : {}),
    ...(card.config ? { config: card.config } : {}),
    ...(card.datasetId ? { datasetId: card.datasetId } : {}),
    ...(card.view ? { view: card.view } : {}),
  };
}
